/**
 * Credential headers for the forms-AI endpoints.
 *
 * These endpoints are reached from TWO worlds that share one component tree:
 *
 *   /forms/fill/:templateId   FormFiller, behind ProtectedRoute  -> Firebase ID token
 *   /forms/public/:slug       PublicFormPage, anonymous          -> capability token
 *   /forms/embed/:slug        EmbedFormPage, anonymous           -> capability token
 *
 * Leaf field components must stay ignorant of which world they are in, so the
 * host injects a credential resolver through PublicFormContext and the leaves
 * just ask for headers. This mirrors how `uploadPhoto` is already injected.
 */

import { auth } from '../../config/firebase';

/** Resolves the credential headers to send with a forms-AI request. */
export type AiCredentialResolver = () => Promise<Record<string, string>>;

/**
 * Default resolver: the signed-in user's Firebase ID token.
 *
 * Returns no headers when nobody is signed in — the request then arrives
 * unauthenticated, which the server rejects once enforcement is on. That is the
 * correct outcome: an in-app surface that reaches this state is a bug to
 * surface, not one to paper over with an anonymous fallback.
 */
export const idTokenCredential: AiCredentialResolver = async (): Promise<Record<string, string>> => {
  const user = auth.currentUser;
  if (!user) return {};
  try {
    return { Authorization: `Bearer ${await user.getIdToken()}` };
  } catch {
    // A token refresh failure must not break the UI — let the request go
    // without a credential and let the server decide.
    return {};
  }
};

/** reCAPTCHA v3 action the server expects on forms-AI calls (functions/src/forms/recaptcha.ts). */
export const FORM_AI_RECAPTCHA_ACTION = 'form_ai';

/** Runs reCAPTCHA v3 for an action and returns its token (react-google-recaptcha-v3). */
export type ExecuteRecaptcha = (action: string) => Promise<string>;

/**
 * Resolver for an anonymous public-form session, built from the capability
 * token minted by `getPublicFormTemplate`.
 *
 * Each call also carries a fresh reCAPTCHA token (#3858): the capability token is free to
 * mint, so it says nothing about whether a person is filling the form. A v3 token is
 * single-use and expires in two minutes, so it is fetched per AI request. When reCAPTCHA
 * is not loaded or fails, the request goes without one and the server decides (it logs a
 * missing token, and refuses it only once enforcement is switched on).
 */
export function formCredential(
  token: string | undefined,
  executeRecaptcha?: ExecuteRecaptcha,
): AiCredentialResolver {
  return async (): Promise<Record<string, string>> => {
    const headers: Record<string, string> = token ? { 'X-Form-Credential': token } : {};
    if (executeRecaptcha) {
      try {
        const recaptchaToken = await executeRecaptcha(FORM_AI_RECAPTCHA_ACTION);
        if (recaptchaToken) headers['X-Recaptcha-Token'] = recaptchaToken;
      } catch {
        // A reCAPTCHA failure must not break the form: send without a token.
      }
    }
    return headers;
  };
}
